import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import {
  ResponsiveContainer,
  ComposedChart,
  XAxis,
  YAxis,
  Tooltip,
  Line,
  ReferenceLine,
  Area,
} from "recharts";
import { store } from "../../redux/store";
import * as patientActions from "../../redux/actions/patientActions";
import { Box, Button, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from "@mui/material";
import CustomTextField from "../../commons/CustomTextField";
import { useToast } from "../../context/ToastContext";
import moment from "moment";
import { socket } from "../../socket/socket";

const getTrimesterData = (data) => {
  return data.reduce((acc, item) => {
    const t = item.week;
    if (!acc[t] || item.week > acc[t].week) {
      acc[t] = { min: item.min, max: item.max, week: item.week };
    }
    return acc;
  }, {});
};

const WeightGainChart = () => {
  const { patientId, consultationId } = useParams();
  const patientReducer = useSelector((state) => state.patientReducer);
  const weightGainChart = patientReducer?.weightGainChart;
  const [lineData, setLineData] = useState([])
  const [areaData, setAreaData] = useState([])
  const [weightData, setWeightData] = useState([])
  const [loader, setLoader] = useState(true)
  const grouped = Array.isArray(weightGainChart?.idealWeightGain)
    ? getTrimesterData(weightGainChart?.idealWeightGain)
    : {};
  const trimesters = [{ value: 0, label: 'T1' }, { value: 13, label: 'T2' }, { value: 40, label: 'T3' }];
  const lowerRow = trimesters.map((t) => grouped[t.value]?.min ?? '-');
  const upperRow = trimesters.map((t) => grouped[t.value]?.max ?? '-');

  const [kg, setKg] = useState("");
  const [grams, setGrams] = useState("");
  const [edit, setEdit] = useState(true)
  const location = useLocation();
  const [projectionData, setProjectionData] = useState([])
  const showToast = useToast()
  const isGynecology = patientReducer?.patientInfo?.patientType == "GYNECOLOGY"
  const [chartData, setChartData] = useState([]);
  const [submitState, setSubmitState] = useState("submit");

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const mode = queryParams.get('mode');
    if (mode) {
      setEdit(mode == "view" ? false : true);
    }
  }, [location.search]);

  const rowStyle = {
    px: 2,
    py: 0.4,
    border: 'none',
    lineHeight: 1,
    fontSize: '11px'
  }

  let motherWeight = patientReducer?.patientInfo?.weight || null;
  let currentWeek = patientReducer?.patientInfo?.currentWeek || null;

  const createData = () => {
    if (!weightGainChart) return;

    if (
      weightGainChart?.idealWeightGain &&
      typeof weightGainChart.idealWeightGain === 'object' &&
      !Array.isArray(weightGainChart.idealWeightGain)
    ) {
      createGynecologyData();
      return;
    }

    createPregnancyData();
  };
  const createPregnancyData = () => {

    const { idealWeightGain: rangeData, weights: motherWeightData } = weightGainChart;
    const weightDetails = [...(motherWeightData || [])].sort((a, b) => a.week - b.week);

    const convertToKg = (value, unit) => (unit === 'G' ? value / 1000 : value);

    const projectionArea = motherWeight ? (rangeData?.map(({ week, max, min, unit }) => {
      const upperLimit = motherWeight + convertToKg(max, unit);
      const lowerLimit = motherWeight + convertToKg(min, unit);
      return { week, range: [lowerLimit, upperLimit] };
    }) || []) : [];

    const rangeMap = (rangeData || []).reduce((acc, item) => {
      acc[item.week] = item;
      return acc;
    }, {});

    const area = [];

    const getRangeWeek = (week) => {
      if (week <= 13) return 0;
      if (week <= 26) return 13;
      if (week === 40) return 40;
      return null;
    };

    if (motherWeight) {
      for (let week = 0; week <= 40; week++) {
        const rangeWeek = getRangeWeek(week);
        if (rangeWeek === null) continue;

        const rangeForWeek = rangeMap[rangeWeek];
        if (!rangeForWeek) continue;

        const upperLimit = motherWeight + convertToKg(rangeForWeek.max, rangeForWeek.unit);
        const lowerLimit = motherWeight + convertToKg(rangeForWeek.min, rangeForWeek.unit);

        area.push({ week, range: [lowerLimit, upperLimit] });
      }
    }

    const lineData = [
      // ...(motherWeight ? [{ week: currentWeek, weight: motherWeight }] : []),
      ...weightDetails,
    ].sort((a, b) => a.week - b.week);

    setLineData(lineData);
    setAreaData(area);
    setWeightData(weightDetails);
    setProjectionData(projectionArea);
  };

  const createGynecologyData = () => {
    const { weights, idealWeightGain } = weightGainChart;

    const sorted = [...(weights || [])].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );

    let minDate = moment();
    let maxDate = moment().add(8, 'days');

    if (sorted.length > 0) {
      minDate = moment(sorted[0].createdAt).startOf('day');
      const actualMax = moment(sorted[sorted.length - 1].createdAt).startOf('day');
      
      const diffDays = actualMax.diff(minDate, 'days');
      if (diffDays < 8) {
        maxDate = minDate.clone().add(8, 'days');
      } else {
        maxDate = actualMax;
      }
    }

    const chart = [];
    let currentDate = minDate.clone();
    
    const weightMap = {};
    sorted.forEach(item => {
      const dateStr = moment(item.createdAt).format("YYYY-MM-DD");
      weightMap[dateStr] = item.weight;
    });

    while (currentDate.isSameOrBefore(maxDate)) {
      const dateKey = currentDate.format("YYYY-MM-DD");
      const displayDate = currentDate.format("DD MMM");
      const lower = idealWeightGain.lowerLimit;
      const upper =  idealWeightGain.upperLimit;

      chart.push({
        date: displayDate,
        weight: weightMap[dateKey] ?? null,
        lower: lower,
        upper: upper,
        range: [lower, upper],
      });
      currentDate.add(1, 'day');
    }

    setChartData(chart);
  };

  useEffect(() => {
    if (patientId) {
      store.dispatch(patientActions.getWeightGainChartRequestAction(patientId));
    }
  }, [patientId]);

  useEffect(() => {
    if (!patientId) return;
    const handleCommandOutput = (data) => {
        console.log("data came on event", data)
        const [, , rawCardType] = data?.data?.originActionType?.split('.');

        let cardType = rawCardType;
        let cardKey = rawCardType;
        if (rawCardType.includes(':')) {
            const [cardCategory, id] = rawCardType.split(':');
            cardType = cardCategory
            cardKey = rawCardType
        }
        if (consultationId !== data?.data?.consultationId) {
            return
        }
        if (
            data?.actionType === "commandOutput.refreshWebDashboard"
        ) {
            if (cardType == "weightGainChart") {
              store.dispatch(patientActions.getWeightGainChartRequestAction(patientId));
            }
        }
    };
    socket.on("commandOutput", handleCommandOutput);
}, [patientId]);

  useEffect(() => {
    if (patientReducer?.weightGainChart) {
      createData()
      setTimeout(() => {
        setLoader(false)
      }, 500);
    }
  }, [patientReducer?.weightGainChart])

  useEffect(() => {
    const weights = patientReducer?.weightGainChart?.weights || [];
    const isWeightAdded = weights.some(
      item =>
        new Date(item.createdAt).toDateString() === new Date().toDateString()
    );
    setSubmitState(isWeightAdded ? "submitted" : "submit");
  }, [patientReducer?.weightGainChart, consultationId]);

  useEffect(() => {
    if (kg || grams) {
      setSubmitState("submit");
    }
  }, [kg, grams]);

  const handleAddWeight = () => {
    store.dispatch(patientActions.addWeightGainChartDataRequestAction(consultationId, `${kg}.${grams}`, patientId))
    setKg("");
    setGrams("");
    showToast("Recorded! ,You can add weight starting next week.", "info");
  };

  const getPregnancyDomains = () => {
    if (motherWeight) {
      return { min: motherWeight - 3, max: motherWeight + 16 };
    }
    if (weightData && weightData.length > 0) {
      const weights = weightData.map(w => w.weight);
      return { min: Math.floor(Math.min(...weights)) - 5, max: Math.ceil(Math.max(...weights)) + 5 };
    }
    return { min: 30, max: 100 };
  }

  const yDomainMin = isGynecology ? Math.floor((weightGainChart?.idealWeightGain?.lowerLimit || 0) - 10) : getPregnancyDomains().min;
  const yDomainMax = isGynecology ? Math.ceil((weightGainChart?.idealWeightGain?.upperLimit || 0) + 10) : getPregnancyDomains().max;

  const yAxisData = [
    { weight: yDomainMin },
    { weight: yDomainMax },
  ];

  console.log("isGynecology ? chartData : areaData.length>0?areaData:yAxisData",isGynecology ? chartData : areaData.length>0?areaData:yAxisData)
  console.log("linedta",lineData)

  return (!loader ? <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
    <Box sx={{ flex: '0 0 auto', display: 'flex', width: '100%' }}>
      {/* Fixed Y-Axis */}
      <Box sx={{ width: '58px', flexShrink: 0, height: 210, zIndex: 1, backgroundColor: 'white' }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={isGynecology ? (weightGainChart?.weights?.length > 0 ? chartData : yAxisData) : areaData.length>0?areaData:yAxisData} margin={{ top: 20, right: 0, left: 0, bottom: 20 }}>
            <YAxis
              width={55}
              tick={{ fill: "#000", fontSize: '8px', fontWeight: 'bold' }}
              label={{
                value: "Weight (kg)",
                angle: -90,
                position: "insideLeft",
                fontSize: '10px',
                fill: "#000",
                fontWeight: 'bold',
                dy: 15,
                dx: 5
              }}
              axisLine={false}
              tickLine={false}
              dataKey="weight"
              domain={[yDomainMin, yDomainMax]}
            />
            {/* Dummy line to force YAxis render */}
            <Line {...(!isGynecology ? { data: lineData } : {})} dataKey="weight" stroke="none" activeDot={false} isAnimationActive={false} />
            {/* Hidden XAxis to maintain vertical alignment */}
            <XAxis
              dataKey={isGynecology ? "date" : "week"}
              tick={{ fill: "transparent" }}
              label={{
                value: isGynecology ? "" : "Week",
                position: "insideBottomRight",
                dy: 12,
                fontSize: '10px',
                fill: "transparent",
              }}
              axisLine={false}
              tickLine={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>

      {/* Scrollable Chart */}
      <Box sx={{ flexGrow: 1, overflowX: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
        <Box sx={{ width: isGynecology ? Math.max(chartData.length * 45, 400) : '100%', minWidth: '100%', height: 210 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={isGynecology ? chartData : areaData.length > 0 ? areaData : [{ week: 0, hiddenWeight: yDomainMin }, { week: 40, hiddenWeight: yDomainMax }]}
              margin={{ top: 20, right: 30, left: 15, bottom: 20 }}>
          {/* X Axis */}
          <XAxis
            dataKey={isGynecology ? "date" : "week"}
            {...(!isGynecology && {
              ticks: [0, 5, 10, 15, 20, 25, 30, 35, 40],
              type: "number",
            })}
            tick={{ fill: "#000", fontSize: '8px', fontWeight: 'bold' }}
            label={{
              value: isGynecology ? "" : "Week",
              position: "insideBottomRight",
              dy: 12,
              fontSize: '10px',
              fill: "#000",
              fontWeight: 'bold',
            }}
            minTickGap={0}
            interval={isGynecology ? "preserveStartEnd" : 0}
          />
          {/* Y Axis (Hidden, only for scaling) */}
          <YAxis
            hide={true}
            dataKey="weight"
            domain={[yDomainMin, yDomainMax]}
          />
          {/* Tooltip */}
          <Tooltip
            formatter={(value, name) => {
              if (name === 'weight') {
                return [`${value} kg`, 'Weight'];
              }
            }}
            contentStyle={{
              backgroundColor: "white",
              border: "1px solid #ddd",
              borderRadius: "6px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
            }}
          />
          {!isGynecology && [{ value: 13, trimester: 'T1' }, { value: 26, trimester: 'T2' }, { value: 40, trimester: 'T3' }].map((item) => {
            return <ReferenceLine
              x={item.value}
              stroke="#606060"
              strokeDasharray="3 3"
              label={{
                value: item.trimester,
                position: 'insideTopRight',
                offset: 20,
                fontSize: '12px',
              }}
            />
          })}
          {!isGynecology && weightData.length > 0 && (
            <>
              <ReferenceLine
                segment={[
                  {
                    x: 0,
                    y: weightData[
                      weightData.length - 1
                    ].weight,
                  },
                  {
                    x: weightData[
                      weightData.length - 1
                    ].week,
                    y: weightData[
                      weightData.length - 1
                    ].weight,
                  },
                ]}
                stroke="#F56423"
                strokeDasharray="3 3"
              />
              <ReferenceLine
                segment={[
                  {
                    x: weightData[
                      weightData.length - 1
                    ].week,
                    y: motherWeight ? motherWeight - 3 : yDomainMin,
                  },
                  {
                    x: weightData[
                      weightData.length - 1
                    ].week,
                    y: weightData[
                      weightData.length - 1
                    ].weight,
                  },
                ]}
                stroke="#FFAF1A"
                strokeWidth={5}
              />
            </>
          )}
          {/* projection area */}
          <Area
            {...(!isGynecology ? { data: projectionData } : {})}
            dataKey="range"
            fill="rgb(253,237, 228)"
            stroke="rgb(253,237, 228)"
            activeDot={false}
          />

          {/*  ploted or captured weight  points */}
          <Line
            {...(!isGynecology ? { data: lineData } : {})}
            dataKey="weight"
            stroke="#F56423"
            strokeWidth={2}
            dot={{ stroke: '#F56423', strokeWidth: 3 }}
            connectNulls
          />
          <Line dataKey="hiddenWeight" stroke="none" activeDot={false} dot={false} isAnimationActive={false} />
          
        </ComposedChart>
      </ResponsiveContainer>
      </Box>
    </Box>

  
  </Box>
  {!isGynecology && <Box sx={{ width: '95%', alignSelf: 'center', flex: '1 1 auto', overflowY: 'auto' }}>
      <TableContainer sx={{
        background: "transparent",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "&::-webkit-scrollbar": {
          display: "none",
        },
      }}>
        <Table
          sx={{ minWidth: 0, width: '100%', background: "transparent" }}
          aria-label="simple table"
        >
          <TableHead>
            <TableRow
              sx={{
                "& td, & th": {
                  border: 0,
                  py: 0.8,
                  fontSize: "10px",
                  lineHeight: 1.2,
                },
              }}
            >
              <TableCell align="right">Recommended Wt. Gain</TableCell>
              {trimesters.map((trimester) => {
                return <TableCell align="center">
                  <Box
                    component="span"
                    sx={{
                      backgroundColor: "#DDEEFD",
                      px: 1,
                      py: 0.5,
                      borderRadius: "6px",
                      display: "inline-block",
                    }}
                  >
                    {trimester.label}
                  </Box>
                </TableCell>
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow
              sx={{
                px: 2,
                py: 0.4,
                border: 'none',
                lineHeight: 1,
                fontSize: '11px'
              }}
            >
              <TableCell align="right" sx={rowStyle}>lower</TableCell>
              {lowerRow.map((row) => (
                <TableCell align="center" sx={rowStyle}>{row}</TableCell>
              ))}
            </TableRow>
            <TableRow sx={rowStyle}>
              <TableCell align="right" sx={rowStyle}>upper</TableCell>
              {upperRow.map((row) => (
                <TableCell align="center" sx={rowStyle}>{row}</TableCell>
              ))}
            </TableRow>
            {edit && <TableRow
              sx={{
                "& td, & th": {
                  border: 0,
                  py: 1,
                  lineHeight: 1.2,
                  fontSize: "10px",
                },
              }}
            >
              <TableCell align="right">Add Weight</TableCell>
              <TableCell align="right" >
                <CustomTextField
                  value={kg}
                  setValue={(childrenValue) => {
                    if (childrenValue === "" || (/^\d+$/.test(childrenValue) && childrenValue.length <= 3)) {
                      setKg(childrenValue);
                    }
                  }}
                  edit={edit}
                  width="50px"
                  placeholder="Kg"
                  inputType="number"
                  maxLength={3}
                />
              </TableCell>
              <TableCell align="right">
                <CustomTextField
                  value={grams}
                  setValue={(childrenValue) => {
                    if (childrenValue === "" || (/^\d+$/.test(childrenValue) && childrenValue.length <= 3)) {
                      setGrams(childrenValue);
                    }
                  }}
                  edit={edit}
                  width="50px"
                  placeholder="g"
                  inputType="number"
                  maxLength={3}
                />
              </TableCell>
              <TableCell align="center">
                <Button
                  variant="contained"
                  disabled={!kg || !grams || !edit ||
                    submitState === "submitted"}
                  onClick={handleAddWeight}
                  sx={{
                    height: '19px', minWidth: '70px', fontSize: '10px', backgroundColor:'#F27704', borderRadius: '13.8px' 
                  }}
                >
                  {submitState === "submitted"
                    ? "Submitted"
                    : "Submit"}
                </Button>
              </TableCell>
            </TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>}
    {isGynecology && <Box sx={{ width: '95%', alignSelf: 'center', flex: '1 1 auto', overflowY: 'auto' }}>
      <TableContainer sx={{
        background: "transparent",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "&::-webkit-scrollbar": {
          display: "none",
        },
      }}>
        <Table
          sx={{ minWidth: 0, width: '100%', background: "transparent" }}
          aria-label="simple table"
        >
          <TableHead>
            <TableRow
              sx={{
                "& td, & th": {
                  border: 0,
                  py: 0.8,
                  fontSize: "10px",
                  lineHeight: 1.2,
                },
              }}
            >
              <TableCell align="right">Recommended Wt.</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow
              sx={{
                px: 2,
                py: 0.4,
                border: 'none',
                lineHeight: 1,
                fontSize: '11px'
              }}
            >
              <TableCell align="right" sx={rowStyle}>lower</TableCell>
              <TableCell align="center" sx={rowStyle}>{weightGainChart?.idealWeightGain?.lowerLimit}</TableCell>
            </TableRow>
            <TableRow sx={rowStyle}>
              <TableCell align="right" sx={rowStyle}>upper</TableCell>
              <TableCell align="center" sx={rowStyle}>{weightGainChart?.idealWeightGain?.upperLimit}</TableCell>
            </TableRow>
            {edit && <TableRow
              sx={{
                "& td, & th": {
                  border: 0,
                  py: 1,
                  lineHeight: 1.2,
                  fontSize: "10px",
                },
              }}
            >
              <TableCell align="right">Add Weight</TableCell>
              <TableCell align="right" >
                <CustomTextField
                  value={kg}
                  setValue={(childrenValue) => {
                    if (childrenValue === "" || (/^\d+$/.test(childrenValue) && childrenValue.length <= 3)) {
                      setKg(childrenValue);
                    }
                  }}
                  edit={edit}
                  width="50px"
                  placeholder="Kg"
                  inputType="number"
                  maxLength={3}
                />
              </TableCell>
              <TableCell align="right">
                <CustomTextField
                  value={grams}
                  setValue={(childrenValue) => {
                    if (childrenValue === "" || (/^\d+$/.test(childrenValue) && childrenValue.length <= 3)) {
                      setGrams(childrenValue);
                    }
                  }}
                  edit={edit}
                  width="50px"
                  placeholder="g"
                  inputType="number"
                  maxLength={3}
                />
              </TableCell>
              <TableCell align="center">
                <Button
                  variant="contained"
                  disabled={!kg || !grams || !edit ||
                    submitState === "submitted"}
                  onClick={handleAddWeight}
                  sx={{
                    height: '19px', minWidth: '70px', fontSize: '10px', backgroundColor: '#F27704', borderRadius: '13.8px'
                  }}
                >
                  {submitState === "submitted"
                    ? "Submitted"
                    : "Submit"}
                </Button>
              </TableCell>
            </TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>}
  </Box>
    :
    <Box sx={{ justifyContent: 'center', height: '180px', alignSelf: 'center', alignContent: 'center' }}>
      <CircularProgress />
    </Box>
  );
};

export default WeightGainChart;
